import {
  questionsAdminControllerCreateOne,
  questionsAdminControllerDeleteOne,
  questionsAdminControllerList,
  questionsAdminControllerUpdateOne,
  type CreateQuestion,
  type Question,
  type UpdateQuestion,
  unwrapApiResponse,
} from "@cryptors/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const getQuestions = async (cryptorId: number): Promise<Question[]> => {
  const filter = `cryptorId||$eq||${cryptorId}`;
  const data = await unwrapApiResponse(
    questionsAdminControllerList<true>({
      query: { filter: [filter], limit: 200 },
      throwOnError: true,
    }),
  );
  return Array.isArray(data) ? data : (data as { data: Question[] }).data;
};

export const useGetQuestions = (cryptorId: number) =>
  useQuery({
    initialData: [],
    queryKey: ["questions", cryptorId],
    queryFn: () => getQuestions(cryptorId),
  });

const postQuestion = async (data: CreateQuestion): Promise<void> => {
  await unwrapApiResponse(
    questionsAdminControllerCreateOne<true>({
      body: data,
      throwOnError: true,
    }),
  );
};

export const usePostQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["questions"],
    mutationFn: postQuestion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questions"] });
    },
  });
};

const patchQuestion = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateQuestion;
}): Promise<void> => {
  await unwrapApiResponse(
    questionsAdminControllerUpdateOne<true>({
      body: data,
      path: { id },
      throwOnError: true,
    }),
  );
};

export const usePatchQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["questions"],
    mutationFn: patchQuestion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({
        queryKey: ["cryptors", "quiz"],
      });
    },
  });
};

const deleteQuestion = async (id: string) => {
  await unwrapApiResponse(
    questionsAdminControllerDeleteOne<true>({
      path: { id },
      throwOnError: true,
    }),
  );
};

export const useDeleteQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["questions"],
    mutationFn: deleteQuestion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questions"] });
      queryClient.invalidateQueries({ queryKey: ["cryptors", "quiz"] });
    },
  });
};
